'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { SpaceMemberRoles } from '@shared/all/domains/spaces';
import AppWidgetContext from '@shared/utils/context/AppWidgetContext';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import formatRelativeTime from '@shared/utils/helpers/formatRelativeTime';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Ban, Edit3 as EditIcon, Expand, Flag, Globe, MoreVertical, Pin, Tag, Trash2, VolumeX, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { animDelay } from '../../utils/animDelay';
import { client } from '../../utils/trpc';
import AwardBadges from '../awards/AwardBadges';
import AwardsList from '../awards/AwardsList';
import Avatar from '../common/Avatar';
import BanUserConfirmation from '../common/BanUserConfirmation';
import BoardBadge from './BoardBadge';
import CrossPostModal from './CrossPostModal';
import EditPost from './EditPost';
import PostDeleteConf from './PostDeleteConf';
import PostFactiiisCount from './PostFactiiisCount';
import PrivateBadge from './PrivateBadge';
import TagUsersModal from './TagUsersModal';
import ViewPostHistory from './ViewPostHistory';
type Props = {
  spaceSlug?: string;
  post: Post;
  isReply?: boolean;
  politics: boolean;
  nsfw: boolean;
  uniqueId?: string;
  isHeadless?: boolean;
  minified?: boolean;
  hideMenu?: boolean;
  externalEditOpen?: boolean;
  onExternalEditClose?: () => void;
  // Needed so the inline "add factiii" (F) button can open the FactiiiModal,
  // which mutates these flags. Absent in preview/headless contexts (hideMenu),
  // where the add button isn't rendered.
  troll?: boolean;
  setNsfw?: (arg0: boolean) => void;
  setPolitics?: (arg0: boolean) => void;
  setTroll?: (arg0: boolean) => void;
  // Board cards pass the list's factiiiId so the card hides that factiii from
  // its own breakdown (it's redundant with the list it sits in).
  ignoreFactiiiId?: number;
  // The card's own URL. When set, the timestamp renders as a real <a href> so
  // crawlers can reach the post from feeds (the card itself opens via a click
  // handler, which is invisible to them). Absent on the post's own page.
  postHref?: string;
};
const PostItemHead = props => {
  const $ = _c(143);
  const {
    pinPostHandler,
    unpinPostHandler,
    setArchivedHandler,
    reportClickHandler,
    blockButtonClickHandler,
    muteButtonClickHandler,
    banSpaceMember,
    openedModal,
    setOpenedModal
  } = usePostsShared(client, props.uniqueId);
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const {
    newModerationReport
  } = useContext(AppWidgetContext);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] !== currentUser.id) {
    t0 = {
      queryKey: ["awards", currentUser.id],
      queryFn: _temp,
      staleTime: Infinity
    };
    $[0] = currentUser.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data: awardsData
  } = useQuery(t0);
  let t1;
  if ($[2] !== awardsData?.awards) {
    t1 = awardsData?.awards ?? [];
    $[2] = awardsData?.awards;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const awards = t1;
  const [repliesCollapsed, setRepliesCollapsed] = useState(false);
  const [optionsDropdownOpen, setOptionsDropdownOpen] = useState(false);
  const optionsDropDownRef = useRef(null);
  const optionsButtonRef = useRef(null);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 0,
      left: 0
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [dropdownPosition, setDropdownPosition] = useState(t2);
  const [awardListOpen, setAwardListOpen] = useState(false);
  const [userMuted, setUserMuted] = useState(false);
  const [userBlock, setUserBlock] = useState(false);
  const pathname = usePathname();
  let t3;
  if ($[5] !== pathname) {
    t3 = pathname?.includes("/user/");
    $[5] = pathname;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isProfilePage = t3;
  let t4;
  if ($[7] !== pathname) {
    t4 = pathname?.includes("/s/");
    $[7] = pathname;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isSpacePage = t4;
  let t5;
  if ($[9] !== currentUser.username || $[10] !== props.post.space) {
    t5 = ["space-membership", props.post.space, currentUser.username, "membership-check"];
    $[9] = currentUser.username;
    $[10] = props.post.space;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props.post.space) {
    t6 = () => unwrapFromResult(client.spaces.getMembership.query({
      slug: props.post.space || ""
    }));
    $[12] = props.post.space;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = Boolean(props.post.space) && Boolean(currentUser.isAuthenticated);
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      staleTime: 300000,
      enabled: t7
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const {
    data: membership
  } = useQuery(t8);
  let t9;
  if ($[18] !== membership?.roles) {
    t9 = Boolean(membership?.roles.includes(SpaceMemberRoles.MODERATOR)) || Boolean(membership?.roles.includes(SpaceMemberRoles.OWNER));
    $[18] = membership?.roles;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const isModerator = t9;
  let t10;
  if ($[20] !== isModerator || $[21] !== membership?.roles) {
    t10 = isModerator || Boolean(membership?.roles.includes(SpaceMemberRoles.MEMBER));
    $[20] = isModerator;
    $[21] = membership?.roles;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const isMember = t10;
  const isBoardLayoutSpace = props.post.pos != null;
  if (props.externalEditOpen && openedModal !== "EDIT") {
    setOpenedModal("EDIT");
    props.onExternalEditClose?.();
  }
  let t11;
  let t12;
  if ($[23] !== optionsDropdownOpen) {
    t11 = () => {
      if (!optionsDropdownOpen) {
        return;
      }
      if (optionsButtonRef.current) {
        const rect = optionsButtonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.right + window.scrollX - 240
        });
      }
      const handleClick = e => {
        if (!optionsDropDownRef.current?.contains(e.target as Node) && !optionsButtonRef.current?.contains(e.target as Node)) {
          setOptionsDropdownOpen(false);
        }
      };
      const handleScroll = () => {
        if (optionsButtonRef.current) {
          const rect_0 = optionsButtonRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect_0.bottom + window.scrollY + 8,
            left: rect_0.right + window.scrollX - 240
          });
        }
      };
      document.addEventListener("click", handleClick);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        document.removeEventListener("click", handleClick);
        window.removeEventListener("scroll", handleScroll, true);
      };
    };
    t12 = [optionsDropdownOpen];
    $[23] = optionsDropdownOpen;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  useEffect(t11, t12);
  let t13;
  if ($[26] !== props.post.triggeredByFactiii || $[27] !== props.post.user) {
    t13 = t14 => {
      const {
        children,
        className
      } = t14;
      return <Link onClick={_temp2} className={className} href={props.post.triggeredByFactiii ? `/f/${props.post.triggeredByFactiii.slug}` : `/user/${props.post.user.username}`}>{children}</Link>;
    };
    $[26] = props.post.triggeredByFactiii;
    $[27] = props.post.user;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const ProfileLink = t13;
  let t14;
  if ($[29] !== props.post.user) {
    t14 = t15 => {
      const {
        children: children_0,
        className: className_0
      } = t15;
      return <Link onClick={_temp3} className={className_0} href={`/user/${props.post.user.username}`}>{children_0}</Link>;
    };
    $[29] = props.post.user;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const UserLink = t14;
  const isAuthor = props.post.isAuthor;
  let t15;
  if ($[31] !== blockButtonClickHandler || $[32] !== currentUser.isAuthenticated || $[33] !== isAuthor || $[34] !== isBoardLayoutSpace || $[35] !== isMember || $[36] !== isModerator || $[37] !== isProfilePage || $[38] !== isSpacePage || $[39] !== muteButtonClickHandler || $[40] !== newModerationReport || $[41] !== openAuthContinueModal || $[42] !== pinPostHandler || $[43] !== props.post || $[44] !== props.spaceSlug || $[45] !== reportClickHandler || $[46] !== setArchivedHandler || $[47] !== setOpenedModal || $[48] !== unpinPostHandler || $[49] !== userBlock || $[50] !== userMuted) {
    t15 = props.post.isPrivate ? [{
      label: "Edit Post",
      icon: <EditIcon size={18} />,
      onClick: () => {
        setOpenedModal("EDIT");
        setOptionsDropdownOpen(false);
      }
    }, {
      label: "Delete Post",
      icon: <Trash2 height={18} width={18} color="red" />,
      onClick: () => {
        setOpenedModal("DELETE_POST_CONFIRMATION");
        setOptionsDropdownOpen(false);
      }
    }] : [{
      label: "Tag Members",
      icon: <Tag height={18} width={18} />,
      onClick: () => {
        if (!currentUser.isAuthenticated) {
          return openAuthContinueModal();
        }
        setOpenedModal("TAG_USERS");
        setOptionsDropdownOpen(false);
      },
      hidden: !isBoardLayoutSpace || !currentUser.isAuthenticated
    }, {
      label: "Edit Post",
      icon: <EditIcon height={18} width={18} />,
      onClick: () => {
        setOpenedModal("EDIT");
        setOptionsDropdownOpen(false);
      },
      hidden: !currentUser.isAuthenticated || (isBoardLayoutSpace ? !isMember : !isAuthor)
    }, {
      label: "Cross-Post",
      icon: <Globe height={18} width={18} />,
      onClick: () => {
        setOpenedModal("CROSS_POST");
        setOptionsDropdownOpen(false);
      },
      hidden: !currentUser.isAuthenticated || !isAuthor
    }, {
      label: props.post.pinned ? "Unpin from profile" : "Pin to profile",
      icon: <Pin height={18} width={18} />,
      onClick: () => {
        setOptionsDropdownOpen(false);
        if (!currentUser.isAuthenticated) {
          return openAuthContinueModal();
        }
        if (props.post.pinned) {
          return unpinPostHandler("profile");
        }
        pinPostHandler("profile", props.post.id);
      },
      hidden: !isAuthor || !isProfilePage
    }, {
      label: props.post.isArchived ? "Restore Post" : "Archive Post",
      icon: props.post.isArchived ? <ArchiveRestore height={18} width={18} /> : <Archive height={18} width={18} />,
      onClick: () => {
        setOptionsDropdownOpen(false);
        setArchivedHandler(props.post.id, !props.post.isArchived);
      },
      hidden: !isAuthor || Boolean(props.post.parentPostId) || props.post.isServerPrivate || props.post.isServerDraft
    }, {
      label: props.post.pinned ? "Unpin from space" : "Pin to space",
      icon: <Pin height={18} width={18} />,
      onClick: () => {
        setOptionsDropdownOpen(false);
        if (!currentUser.isAuthenticated) {
          return openAuthContinueModal();
        }
        if (props.post.pinned) {
          return unpinPostHandler("space", props.spaceSlug);
        }
        pinPostHandler("space", props.post.id, props.spaceSlug);
      },
      hidden: !isSpacePage || !isModerator
    }, {
      label: userMuted ? "Unmute" : "Mute",
      icon: <VolumeX height={18} width={18} />,
      onClick: () => muteButtonClickHandler(userMuted, setUserMuted, props.post.user.username),
      hidden: !currentUser.isAuthenticated || isAuthor
    }, {
      label: userBlock ? "Unblock" : "Block",
      icon: <Ban height={18} width={18} />,
      onClick: () => blockButtonClickHandler(userMuted, setUserBlock, props.post.user.username),
      hidden: !currentUser.isAuthenticated || isAuthor
    }, {
      label: "Report",
      icon: <Flag height={18} width={18} />,
      onClick: () => reportClickHandler("post", props.post, openAuthContinueModal, newModerationReport),
      hidden: !currentUser.isAuthenticated || isAuthor
    }, {
      label: "Ban User From Space",
      icon: <Ban height={18} width={18} />,
      onClick: () => {
        setOpenedModal("BAN_USER_CONFIRMATION");
        setOptionsDropdownOpen(false);
      },
      hidden: !isModerator || isAuthor
    }, {
      label: "Delete Post",
      icon: <Trash2 height={18} width={18} color="red" />,
      onClick: () => {
        setOpenedModal("DELETE_POST_CONFIRMATION");
        setOptionsDropdownOpen(false);
      },
      hidden: !currentUser.isAuthenticated || !(isAuthor || isBoardLayoutSpace && isModerator)
    }];
    $[31] = blockButtonClickHandler;
    $[32] = currentUser.isAuthenticated;
    $[33] = isAuthor;
    $[34] = isBoardLayoutSpace;
    $[35] = isMember;
    $[36] = isModerator;
    $[37] = isProfilePage;
    $[38] = isSpacePage;
    $[39] = muteButtonClickHandler;
    $[40] = newModerationReport;
    $[41] = openAuthContinueModal;
    $[42] = pinPostHandler;
    $[43] = props.post;
    $[44] = props.spaceSlug;
    $[45] = reportClickHandler;
    $[46] = setArchivedHandler;
    $[47] = setOpenedModal;
    $[48] = unpinPostHandler;
    $[49] = userBlock;
    $[50] = userMuted;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  const options = t15;
  const hasVisibleOptions = options.some(_temp4);
  let t16;
  if ($[52] !== awards || $[53] !== props.post.awardCounts) {
    t16 = () => {
      if (!props.post.awardCounts || props.post.awardCounts?.length === 0) {
        return null;
      }
      return <div onClick={e_2 => {
        e_2.stopPropagation();
        setAwardListOpen(true);
      }} className="anim-enter cursor-pointer [--enter-duration:300ms] [--enter-scale:0.9]"><AwardBadges size="xs" tiers={props.post.awardCounts.map(awardCount => {
          const matchingAward = awards.find(award => award.factiiiId === awardCount.factiiiId);
          return {
            image: matchingAward?.image ?? null,
            title: matchingAward?.tron.title ?? "",
            count: awardCount.count,
            quantity: matchingAward?.tron.quantity ?? 0
          };
        }).sort(_temp5)} /></div>;
    };
    $[52] = awards;
    $[53] = props.post.awardCounts;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const RenderRewards = t16;
  const hasAwards = (props.post.awardCounts?.length ?? 0) > 0;
  let t17;
  if ($[55] !== props.hideMenu || $[56] !== props.ignoreFactiiiId || $[57] !== props.minified || $[58] !== props.nsfw || $[59] !== props.politics || $[60] !== props.post || $[61] !== props.setNsfw || $[62] !== props.setPolitics || $[63] !== props.setTroll || $[64] !== props.troll || $[65] !== props.uniqueId) {
    t17 = () => {
      if (props.hideMenu) {
        return null;
      }
      return <PostFactiiisCount post={props.post} ignorePostFactiiiIds={[]} ignoreFactiiiId={props.ignoreFactiiiId} nsfw={props.nsfw} politics={props.politics} troll={props.troll} setNsfw={props.setNsfw} setPolitics={props.setPolitics} setTroll={props.setTroll} uniqueId={props.uniqueId} minified={props.minified} />;
    };
    $[55] = props.hideMenu;
    $[56] = props.ignoreFactiiiId;
    $[57] = props.minified;
    $[58] = props.nsfw;
    $[59] = props.politics;
    $[60] = props.post;
    $[61] = props.setNsfw;
    $[62] = props.setPolitics;
    $[63] = props.setTroll;
    $[64] = props.troll;
    $[65] = props.uniqueId;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  const renderFactiiiCluster = t17;
  let t18;
  if ($[67] !== ProfileLink || $[68] !== RenderRewards || $[69] !== UserLink || $[70] !== hasAwards || $[71] !== hasVisibleOptions || $[72] !== isBoardLayoutSpace || $[73] !== optionsDropdownOpen || $[74] !== props.hideMenu || $[75] !== props.isHeadless || $[76] !== props.isReply || $[77] !== props.minified || $[78] !== props.nsfw || $[79] !== props.post.createdAt || $[80] !== props.post.edited || $[81] !== props.post.id || $[82] !== props.post.isPrivate || $[83] !== props.post.isServerPrivate || $[84] !== props.post.pinned || $[85] !== props.post.space || $[86] !== props.post.taggedUsers || $[87] !== props.post.triggeredByFactiii || $[88] !== props.post.user || $[89] !== props.postHref || $[90] !== props.spaceSlug || $[91] !== renderFactiiiCluster || $[92] !== repliesCollapsed || $[93] !== setOpenedModal) {
    t18 = props.minified ? <div className="flex items-center justify-between gap-2"><div className="flex min-w-0 flex-1 items-center gap-2" onClick={_temp6}>{props.post.taggedUsers && props.post.taggedUsers.length > 0 && <div className="flex shrink-0 items-center -space-x-1.5">{props.post.taggedUsers.slice(0, 4).map(_temp7)}{props.post.taggedUsers.length > 4 && <div className="relative z-0 flex size-5 items-center justify-center rounded-full bg-background-secondary text-[9px] font-emphasis text-textSecondary ring-1 ring-background-light">+{props.post.taggedUsers.length - 4}</div>}</div>}{renderFactiiiCluster()}</div><div ref={optionsDropDownRef} className="relative flex shrink-0 flex-row items-center justify-center gap-2">{(props.post.isPrivate || props.post.isServerPrivate) && <PrivateBadge postId={props.post.id} serverStored={props.post.isServerPrivate} />}{!props.hideMenu && hasVisibleOptions && <div className="relative">{optionsDropdownOpen ? <button ref={optionsButtonRef} title="Close Options Dropdown" onClick={e_4 => {
            e_4.stopPropagation();
            setOptionsDropdownOpen(false);
          }} onMouseDown={_temp8} className="anim-enter rounded-full bg-background-secondary p-1 transition-colors duration-200 [--enter-duration:200ms] [--enter-rotate:-90deg] hover:bg-background-light/80"><X size={16} /></button> : <button ref={optionsButtonRef} title="Open Options Dropdown" onClick={e_6 => {
            e_6.stopPropagation();
            setOptionsDropdownOpen(true);
          }} onMouseDown={_temp9} className="rounded-full p-1 transition-all duration-200 hover:scale-[1.05] hover:bg-background-light"><MoreVertical size={16} /></button>}</div>}</div></div> : <div className="flex space-x-2">{props.isHeadless && <div className="relative flex flex-col items-center pt-1"><div className="absolute -top-8 h-8 w-0.5 bg-gradient-to-b from-ds-border-standard via-ds-border-subtle to-transparent" /><div className="relative z-10 size-2 rounded-full bg-background-light shadow-sm ring-2 ring-background-light" /></div>}<div className="min-w-0 flex-1"><div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-textSecondary/60">{props.spaceSlug !== props.post.space && props.post.space && !props.isReply && <Link onClick={_temp10} className="transition-colors duration-200 hover:text-textSecondary" href={`/s/${props.post.space}`}><p>Posted at {`s/${props.post.space}`}</p></Link>}{isBoardLayoutSpace && props.post.space && <BoardBadge spaceSlug={props.post.space} />}{props.spaceSlug !== props.post.space && props.post.space && (props.post.pinned || props.nsfw) && <p className="mx-1">•</p>}{props.post.pinned && <p className="flex items-center"><Pin height={14} width={14} /><span>Pinned Post</span></p>}{!props.spaceSlug && props.post.pinned && props.nsfw && <p className="mx-1">•</p>}</div><div className="flex min-w-0 items-center"><div className={`flex min-w-0 items-center ${props.isHeadless ? "flex-1 gap-3" : "flex-1 justify-start space-x-2"}`}>{repliesCollapsed && <button title="Collapse Replies" className="anim-enter mr-2 rounded-full p-1 transition-colors duration-200 [--enter-scale:0.9] hover:bg-background-light" onClick={e_9 => {
              e_9.stopPropagation();
              setRepliesCollapsed(false);
            }}><Expand height={15} width={15} /></button>}{!props.isReply && !props.isHeadless && <ProfileLink className="shrink-0 self-center">{props.post.triggeredByFactiii ? <Avatar factiiiAvatarKey={props.post.triggeredByFactiii.avatarKey} factiiiId={props.post.triggeredByFactiii.id} size={35} /> : <Avatar robohash={props.post.user.robohash} avatarKey={props.post.user.avatarKey} isBot={props.post.user.tag === "BOT"} size={35} userId={props.post.user.id} isAnonymous={props.post.user.anonymous} />}</ProfileLink>}<div className={`flex min-w-0 flex-col ${props.isHeadless ? "flex-1 pt-0.5" : "flex-1"}`}>{!props.isHeadless && <div className="flex min-w-0 items-center gap-x-2"><ProfileLink className="min-w-0 flex-1">{props.post.triggeredByFactiii ? <div className="flex min-w-0 items-center gap-1.5"><span className="block min-w-0 truncate font-emphasis transition-all duration-200 hover:underline">{props.post.triggeredByFactiii.name}</span><div className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-emphasis text-primary"><svg className="size-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V7H9V9H3V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V9H21ZM7 15V13H17V15H7Z" /></svg><span>Bot</span></div></div> : !props.post.user.name ? <span className="block min-w-0 truncate font-emphasis transition-all duration-200 hover:underline">@{props.post.user.username}</span> : <p className="mr-1 block min-w-0 truncate font-semibold transition-all duration-200 hover:underline">{props.post.user.name}</p>}</ProfileLink><div className="hidden sm:flex"><RenderRewards /></div></div>}<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-textSecondary/70">{!props.isHeadless && <>{props.post.triggeredByFactiii ? <><UserLink className="min-w-0 flex-shrink"><span className="block min-w-0 truncate transition-colors duration-200 hover:text-textSecondary">posted through @{props.post.user.username}</span></UserLink><span>•</span></> : props.post.user.name && <><ProfileLink className="min-w-0 flex-shrink"><span className="block min-w-0 truncate transition-colors duration-200 hover:text-textSecondary">@{props.post.user.username}</span></ProfileLink><span>•</span></>}</>}{props.postHref ? <Link href={props.postHref} className="flex shrink-0 items-center gap-x-2 whitespace-nowrap transition-colors duration-200 hover:text-textSecondary" onClick={_temp11}><time dateTime={new Date(props.post.createdAt).toISOString()}>{formatRelativeTime(new Date(props.post.createdAt))}</time></Link> : <p className="flex shrink-0 items-center gap-x-2 whitespace-nowrap">{formatRelativeTime(new Date(props.post.createdAt))}</p>}{props.isHeadless && <RenderRewards />}{props.post.edited && <p className="flex shrink-0 items-center gap-x-1 whitespace-nowrap"><span>•</span><span className="cursor-pointer transition-colors duration-200 hover:text-textSecondary" onClick={e_11 => {
                    e_11.stopPropagation();
                    setOpenedModal("HISTORY");
                  }}>edited</span></p>}{!props.hideMenu && <div className="contents" onClick={_temp12}>{renderFactiiiCluster()}</div>}</div>{!props.isHeadless && hasAwards && <div className="mt-1 flex items-center sm:hidden"><RenderRewards /></div>}</div></div></div></div><div ref={optionsDropDownRef} className="relative flex shrink-0 flex-row items-center justify-center gap-2">{(props.post.isPrivate || props.post.isServerPrivate) && <PrivateBadge postId={props.post.id} serverStored={props.post.isServerPrivate} />}{!props.hideMenu && hasVisibleOptions && <div className="relative">{optionsDropdownOpen ? <button ref={optionsButtonRef} title="Close Options Dropdown" onClick={e_13 => {
            e_13.stopPropagation();
            setOptionsDropdownOpen(false);
          }} onMouseDown={_temp13} className="anim-enter rounded-full bg-background-secondary p-1 transition-colors duration-200 [--enter-duration:200ms] [--enter-rotate:-90deg] hover:bg-background-light/80"><X size={16} /></button> : <button ref={optionsButtonRef} title="Open Options Dropdown" onClick={e_15 => {
            e_15.stopPropagation();
            setOptionsDropdownOpen(true);
          }} onMouseDown={_temp14} className="rounded-full p-1 transition-all duration-200 hover:scale-[1.05] hover:bg-background-light"><MoreVertical size={16} /></button>}</div>}</div></div>;
    $[67] = ProfileLink;
    $[68] = RenderRewards;
    $[69] = UserLink;
    $[70] = hasAwards;
    $[71] = hasVisibleOptions;
    $[72] = isBoardLayoutSpace;
    $[73] = optionsDropdownOpen;
    $[74] = props.hideMenu;
    $[75] = props.isHeadless;
    $[76] = props.isReply;
    $[77] = props.minified;
    $[78] = props.nsfw;
    $[79] = props.post.createdAt;
    $[80] = props.post.edited;
    $[81] = props.post.id;
    $[82] = props.post.isPrivate;
    $[83] = props.post.isServerPrivate;
    $[84] = props.post.pinned;
    $[85] = props.post.space;
    $[86] = props.post.taggedUsers;
    $[87] = props.post.triggeredByFactiii;
    $[88] = props.post.user;
    $[89] = props.postHref;
    $[90] = props.spaceSlug;
    $[91] = renderFactiiiCluster;
    $[92] = repliesCollapsed;
    $[93] = setOpenedModal;
    $[94] = t18;
  } else {
    t18 = $[94];
  }
  let t19;
  if ($[95] !== dropdownPosition || $[96] !== options || $[97] !== optionsDropdownOpen) {
    t19 = optionsDropdownOpen && typeof document !== "undefined" && createPortal(<div ref={optionsDropDownRef} className="z-popover" style={{
      position: "absolute",
      top: dropdownPosition.top,
      left: dropdownPosition.left
    }} onClick={_temp15} onMouseDown={_temp16} onPointerDown={_temp17}><div className="anim-enter max-h-60 w-60 space-y-1 overflow-y-auto rounded-md border border-ds-border-standard bg-background-primary p-2 shadow-lg [--enter-duration:200ms] [--enter-y:10px]">{options.filter(_temp18).map(_temp20)}</div></div>, document.body);
    $[95] = dropdownPosition;
    $[96] = options;
    $[97] = optionsDropdownOpen;
    $[98] = t19;
  } else {
    t19 = $[98];
  }
  let t20;
  if ($[99] !== openedModal || $[100] !== props.post.id || $[101] !== props.post.isPrivate || $[102] !== props.post.user || $[103] !== setOpenedModal) {
    t20 = openedModal === "HISTORY" && <ViewPostHistory closeHandler={() => setOpenedModal(undefined)} avatarKey={props.post.user.avatarKey} robohash={props.post.user.robohash} isBot={props.post.user.tag === "BOT"} userId={props.post.user.id} postId={props.post.id} isPrivate={props.post.isPrivate} />;
    $[99] = openedModal;
    $[100] = props.post.id;
    $[101] = props.post.isPrivate;
    $[102] = props.post.user;
    $[103] = setOpenedModal;
    $[104] = t20;
  } else {
    t20 = $[104];
  }
  let t21;
  if ($[105] !== banSpaceMember || $[106] !== openedModal || $[107] !== props.post.user || $[108] !== props.spaceSlug || $[109] !== setOpenedModal) {
    t21 = openedModal === "BAN_USER_CONFIRMATION" && <BanUserConfirmation closeHandler={() => setOpenedModal(undefined)} confirmHandler={ruleId => banSpaceMember(props.spaceSlug, props.post.user.username, ruleId)} spaceSlug={props.spaceSlug || ""} />;
    $[105] = banSpaceMember;
    $[106] = openedModal;
    $[107] = props.post.user;
    $[108] = props.spaceSlug;
    $[109] = setOpenedModal;
    $[110] = t21;
  } else {
    t21 = $[110];
  }
  let t22;
  if ($[111] !== openedModal || $[112] !== props.post.id || $[113] !== props.post.isPrivate || $[114] !== props.post.isServerPrivate || $[115] !== setOpenedModal) {
    t22 = openedModal === "DELETE_POST_CONFIRMATION" && <PostDeleteConf closeHandler={() => {
      setOpenedModal(undefined);
      setOptionsDropdownOpen(false);
    }} isDraft={false} postId={props.post.id} isPrivate={props.post.isPrivate} isServerPrivate={props.post.isServerPrivate} />;
    $[111] = openedModal;
    $[112] = props.post.id;
    $[113] = props.post.isPrivate;
    $[114] = props.post.isServerPrivate;
    $[115] = setOpenedModal;
    $[116] = t22;
  } else {
    t22 = $[116];
  }
  let t23;
  if ($[117] !== openedModal || $[118] !== props.post || $[119] !== props.uniqueId || $[120] !== setOpenedModal) {
    t23 = openedModal === "EDIT" && <EditPost closeHandler={() => setOpenedModal(undefined)} post={props.post} uniqueId={props.uniqueId} />;
    $[117] = openedModal;
    $[118] = props.post;
    $[119] = props.uniqueId;
    $[120] = setOpenedModal;
    $[121] = t23;
  } else {
    t23 = $[121];
  }
  let t24;
  if ($[122] !== openedModal || $[123] !== props.post || $[124] !== setOpenedModal) {
    t24 = openedModal === "TAG_USERS" && <TagUsersModal post={props.post} closeHandler={() => setOpenedModal(undefined)} />;
    $[122] = openedModal;
    $[123] = props.post;
    $[124] = setOpenedModal;
    $[125] = t24;
  } else {
    t24 = $[125];
  }
  let t25;
  if ($[126] !== openedModal || $[127] !== props.post.id || $[128] !== setOpenedModal) {
    t25 = openedModal === "CROSS_POST" && <CrossPostModal postId={props.post.id} closeHandler={() => setOpenedModal(undefined)} />;
    $[126] = openedModal;
    $[127] = props.post.id;
    $[128] = setOpenedModal;
    $[129] = t25;
  } else {
    t25 = $[129];
  }
  let t26;
  if ($[130] !== awardListOpen || $[131] !== props.post.id) {
    t26 = awardListOpen && <AwardsList postId={props.post.id} closeHandler={() => setAwardListOpen(false)} />;
    $[130] = awardListOpen;
    $[131] = props.post.id;
    $[132] = t26;
  } else {
    t26 = $[132];
  }
  let t27;
  if ($[133] !== t18 || $[134] !== t19 || $[135] !== t20 || $[136] !== t21 || $[137] !== t22 || $[138] !== t23 || $[139] !== t24 || $[140] !== t25 || $[141] !== t26) {
    t27 = <>{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}</>;
    $[133] = t18;
    $[134] = t19;
    $[135] = t20;
    $[136] = t21;
    $[137] = t22;
    $[138] = t23;
    $[139] = t24;
    $[140] = t25;
    $[141] = t26;
    $[142] = t27;
  } else {
    t27 = $[142];
  }
  return t27;
};
export default PostItemHead;
function _temp() {
  return unwrapFromResult(client.awards.list.query());
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(option) {
  return !option.hidden;
}
function _temp5(a, b) {
  return b.quantity - a.quantity;
}
function _temp6(e_3) {
  return e_3.stopPropagation();
}
function _temp7(user) {
  return <div key={user.id} className="relative z-10 inline-block rounded-full ring-1 ring-background-light"><Avatar avatarKey={user.avatarKey ?? null} robohash={user.robohash ?? ""} isBot={user.tag === "BOT"} size={20} userId={user.id} /></div>;
}
function _temp8(e_5) {
  return e_5.stopPropagation();
}
function _temp9(e_7) {
  return e_7.stopPropagation();
}
function _temp10(e_8) {
  return e_8.stopPropagation();
}
function _temp11(e_10) {
  if (e_10.metaKey || e_10.ctrlKey || e_10.shiftKey || e_10.altKey) {
    e_10.stopPropagation();
    return;
  }
  e_10.preventDefault();
}
function _temp12(e_12) {
  return e_12.stopPropagation();
}
function _temp13(e_14) {
  return e_14.stopPropagation();
}
function _temp14(e_16) {
  return e_16.stopPropagation();
}
function _temp15(e_17) {
  return e_17.stopPropagation();
}
function _temp16(e_18) {
  return e_18.stopPropagation();
}
function _temp17(e_19) {
  return e_19.stopPropagation();
}
function _temp18(option_0) {
  return !option_0.hidden;
}
function _temp19(e_21) {
  return e_21.stopPropagation();
}
function _temp20(option_1, index) {
  return <button key={index} style={animDelay(index * 30)} className="anim-enter flex w-full items-center rounded-md p-2 text-left text-sm text-textSecondary/70 transition-colors duration-200 [--enter-duration:200ms] [--enter-x:-10px] hover:bg-background-light" onClick={e_20 => {
    e_20.stopPropagation();
    option_1.onClick();
  }} onMouseDown={_temp19}>{option_1.icon && <span className="mr-2">{option_1.icon}</span>}<span className="text-base">{option_1.label}</span></button>;
}